import { action, autorun, makeObservable, observable } from 'mobx';
import { BetslipStateLegsType } from 'src/domains/sportsbook/betting/state/betSlipState/LegStateType';
import { ErrorType } from 'src/domains/sportsbook/betting/betSlipState/BetSlipSheredTypes';
import { SelectionId } from 'src_common/common/websocket2/id/WebsocketId';
import { Amount } from 'src_common/common/amount/Amount';
import { QuickBetState } from './quickBetState/QuickBetState';
import { ReferralState } from './ReferralState';
import { SelectionModel } from 'src_common/common/websocket2/models/SelectionModel/SelectionModel';

import { PriceType } from 'src_common/common/websocket2/modelsApi/Market';
import { CombinationInputType } from 'src/domains/sportsbook/betting/state/betSlipState/CombinationsStateType';
import { AutoMap, AutoMapSerialized } from 'src_common/common/mobx-utils/AutoMap';
import {
    BetslipAllSinglesId,
    BetslipCombinationId,
    BetslipRabId,
    BetslipSingleId,
    BetslipMultibetId,
} from 'src/domains/sportsbook/betting/models/BetslipIdModels';
import { assertNever } from 'src_common/common/assertNever';
import { StakeInputState2 } from './stakeInputState/StakeInputState2';
import { Value } from 'src_common/common/mobx-utils/Value';
import { BetslipIdsFactory } from 'src/domains/sportsbook/betting/models/BetslipIdsFactory';
import { GoogleTagManagerState } from 'src/domains/layouts/state/googleState/GoogleTagManagerState';
import { Common } from 'src/domains/common/Common';

interface BetRefferalLegUpdateType {
    stakePerLine: string | undefined | null;
    price:
        | undefined
        | null
        | {
              d: number;
              f: string;
          };
}

interface BetRefferalCombinationsUpdateType {
    stakePerLine: string | null;
}

class RefItem {
    @observable public refSelection: HTMLElement | null = null;

    public constructor() {
        makeObservable(this);
    }
    @action public setRef = (ref: HTMLElement | null): void => {
        this.refSelection = ref;
    };
}
// refSelection

type BetslipIdType = BetslipSingleId | BetslipCombinationId | BetslipRabId | BetslipAllSinglesId | BetslipMultibetId;

const betslipIdSerialize = (id: BetslipIdType): string => {
    switch (id.type) {
        case 'all':
            return 'all';

        case 'single':
            return ['single', id.reactKey].join('_');

        case 'combination':
            return ['combination', id.reactKey].join('_');

        case 'rab':
            return ['rab', id.reactKey].join('_');

        case 'multibet':
            return ['multibet', id.reactKey].join('_');

        default:
            return assertNever('', id);
    }
};

const newInputsCombination = (
    decimalLength: number,
    onChangeCombinationStake: (id: string, newValue: string) => void
): Value<AutoMap<string, StakeInputState2>> => {
    return new Value(
        new AutoMap(
            (id) =>
                new StakeInputState2(decimalLength, (newValue: Amount) => {
                    onChangeCombinationStake(id, newValue.value);
                })
        )
    );
};

export class BetslipData {
    private mapCorePrepareLegsValue: Value<Map<BetslipSingleId, BetslipStateLegsType>>;
    private mapCorePrepareCombinations: Value<Map<string, CombinationInputType>>; //TODO - string -> BetslipCombinationId
    private readonly refs: AutoMapSerialized<BetslipIdType, RefItem>;
    private inputsCombination: Value<AutoMap<string, StakeInputState2>>; //TODO - string -> BetslipCombinationId
    // public readonly onGoogleTagManagerBetAddedTag: EventEmmiter<{
    //     selectionPrice: PriceType | undefined;
    //     marketId: number;
    //     marketName: string;
    //     selectionId: number;
    //     selectionName: string;
    //     betEventId: number;
    //     betEventName: string;
    //     type: string;
    //     sportId: string;
    //     sportName: string;
    // }>;

    public getMapCorePrepareLegsValue(): Map<BetslipSingleId, BetslipStateLegsType> {
        return this.mapCorePrepareLegsValue.getValue();
    }

    public constructor(
        private readonly ids: BetslipIdsFactory,
        private readonly quickBetState: QuickBetState,
        private readonly referralState: () => ReferralState,
        private readonly decimalLength: number,
        private readonly common: Common
    ) {
        makeObservable(this);

        this.mapCorePrepareLegsValue = new Value(new Map());
        // this.onGoogleTagManagerBetAddedTag = new EventEmmiter();
        this.mapCorePrepareCombinations = new Value(new Map());
        this.refs = new AutoMapSerialized(betslipIdSerialize, () => new RefItem());

        // this.inputsSingle = newInputsSingle(this.decimalLength, this.onChangeStake);
        this.inputsCombination = newInputsCombination(this.decimalLength, this.onChangeCombinationStake);
    }

    /**
     * @deprecated - this method should be removed in the next version
     * @param selectionId
     * @returns
     */
    public convertOldSelectionIdToNewId = (
        oldSelectionId: number,
        refferalIndex?: number
    ): Promise<BetslipSingleId> => {
        return new Promise((resolve) => {
            autorun((dispose) => {
                const selectionModel = $appState.appSportsBookState.models.getSelection(oldSelectionId);

                if (selectionModel === null) {
                    return;
                }

                dispose.dispose();

                const id = this.ids.getSingleId(selectionModel.id2, refferalIndex ?? 0);
                resolve(id);
            });
        });
    };

    private onResetCombinations(): void {
        this.mapCorePrepareCombinations = new Value(new Map());
        this.inputsCombination = newInputsCombination(this.decimalLength, this.onChangeCombinationStake);
    }

    public getInputSingle(selectionId: BetslipSingleId): StakeInputState2 | null {
        const item = this.mapCorePrepareLegsValue.getValue().get(selectionId);
        if (item === undefined) {
            return null;
        }

        return item.stakePerLine;
    }

    public getInputCombination(combinationId: string): StakeInputState2 {
        return this.inputsCombination.getValue().get(combinationId);
    }

    private mutateLegs(callback: (map: Map<BetslipSingleId, BetslipStateLegsType>) => void): void {
        const map = this.mapCorePrepareLegsValue.getValue();
        callback(map);
        this.mapCorePrepareLegsValue.setValue(map);
        if (map.size <= 1) {
            this.ids.getAll().getModel().stakeInput.reset();
        }
    }

    private mutateLegsItemIfExist(
        betId: BetslipSingleId,
        callback: (item: BetslipStateLegsType) => BetslipStateLegsType
    ): void {
        const map = this.mapCorePrepareLegsValue.getValue();
        const item = map.get(betId);
        if (item === undefined) {
            console.error(`mutateLegsItemIfExist -> not exist betId=${betId.reactKey}`);
        } else {
            const newItem = callback(item);
            map.set(betId, newItem);
            this.mapCorePrepareLegsValue.setValue(map);
        }
    }

    private mutateCombinationsItem(
        combinationsId: string,
        callback: (item: CombinationInputType) => CombinationInputType
    ): void {
        const map = this.mapCorePrepareCombinations.getValue();
        const item: CombinationInputType = map.get(combinationsId) ?? { stakePerLine: '0', eachWay: false };

        const newItem = callback(item);
        map.set(combinationsId, newItem);
        this.mapCorePrepareCombinations.setValue(map);
    }

    private getSelectionPriceByType = (
        selectionModel: SelectionModel,
        priceType: 'fp' | 'sp' | 'boosted'
    ): PriceType | null => {
        if (priceType === 'boosted') {
            return selectionModel.boostedPrice ?? selectionModel.price ?? null;
        }
        if (priceType === 'sp') {
            return selectionModel.spPrice ?? null;
        }
        return selectionModel.price ?? null;
    };

    private normalizePriceType = (
        selectionModel: SelectionModel,
        priceType: 'fp' | 'sp' | 'boosted'
    ): 'fp' | 'sp' | 'boosted' => {
        if (selectionModel.spOnly === true) {
            return 'sp';
        }
        if (priceType === 'boosted' && selectionModel.boostedPrice === null) {
            return 'fp';
        }
        return priceType;
    };

    private updateLegPriceTypeAndPrice = (betId: BetslipSingleId, priceType: 'fp' | 'sp' | 'boosted'): void => {
        this.mutateLegsItemIfExist(betId, (legItem): BetslipStateLegsType => {
            const selectionModel = legItem.selectionId.getModel();

            if (selectionModel === null) {
                return {
                    ...legItem,
                    priceType,
                };
            }

            const normalizedPriceType = this.normalizePriceType(selectionModel, priceType);

            return {
                ...legItem,
                priceType: normalizedPriceType,
                selectionPrice: this.getSelectionPriceByType(selectionModel, normalizedPriceType),
            };
        });
    };

    @action public onChangePriceType = (betId: BetslipSingleId, price: 'fp' | 'sp' | 'boosted'): void => {
        this.updateLegPriceTypeAndPrice(betId, price);
    };

    @action public onUpdateError = async (betId: number, errors: Array<ErrorType>): Promise<void> => {
        const id = await this.convertOldSelectionIdToNewId(betId);
        this.mutateLegsItemIfExist(id, (legItem): BetslipStateLegsType => {
            return {
                ...legItem,
                errors: errors,
            };
        });
    };

    private async triggerGoogle(legModel: BetslipStateLegsType): Promise<void> {
        const selection = await legModel.selectionId.getModelAsync();
        const market = await legModel.selectionId.getMarketId().getModelAsync();
        const event = await legModel.selectionId.getEventId().getEventModelAsync();
        const googleTagManager = GoogleTagManagerState.get(this.common);
        googleTagManager.betAddedTag({
            selectionPrice: selection.price,
            marketId: market.id2.toOldId(),
            marketName: market.name,
            selectionId: selection.id2.toOldId(),
            selectionName: selection.name,
            betEventId: event.id2.toOldId(),
            betEventName: event.name,
            type: selection.isSP ? 'sp' : 'fp',
            sportId: event.sportOriginal,
            sportName: event.sport,
        });
    }

    @action private justAddBet = (
        selectionId: SelectionId,
        priceType: 'fp' | 'sp' | 'boosted',
        index: number | null
    ): void => {
        console.info('justAddBet');

        const betId = this.ids.getSingleId(selectionId, 0);
        const selectionModel = selectionId.getModel();

        if (selectionModel === null) {
            console.error('BetslipData.justAddBet -> no selectionModel loaded');
            return;
        }

        const normalizedPriceType = this.normalizePriceType(selectionModel, priceType);
        const selectionSuspended =
            selectionModel.forView(normalizedPriceType === 'sp', normalizedPriceType === 'boosted')?.suspended ?? false;
        const { isReferred } = this.referralState();
        const isExtraPlacesMarket = selectionModel.templateMarketId === 'each-way-extra'; //TODO: replace the name with extra-places when its changed on the backend side

        if (selectionSuspended || isReferred) {
            console.warn('BetslipData.justAddBet ignore user event', { selectionSuspended, isReferred });
            return;
        }

        const coreLegsPossibleBetsRequestItem: BetslipStateLegsType = {
            selectionId,
            priceType: normalizedPriceType,
            stakePerLine: new StakeInputState2(this.decimalLength, () => {}),
            eachWay: isExtraPlacesMarket,
            index,
            selectionPrice: this.getSelectionPriceByType(selectionModel, normalizedPriceType),
            errors: [],
        };

        this.mutateLegs((map) => {
            map.set(betId, coreLegsPossibleBetsRequestItem);

            if (map.size === 1) {
                this.quickBetState.onUpdateQuickBet(true);
            } else {
                this.quickBetState.onUpdateQuickBet(false);
            }
        });

        this.onResetCombinations();

        this.triggerGoogle(coreLegsPossibleBetsRequestItem).catch((error) => {
            console.error(error);
        });
    };

    /**
     * This method is used by external operators. Do not remove it without informing others.
     */
    @action public onAddSimpleBet = (betIdNew: SelectionId): void => {
        const betId = this.ids.getSingleId(betIdNew, 0);

        const betItem = this.mapCorePrepareLegsValue.getValue().get(betId);
        if (betItem === undefined) {
            this.justAddBet(betIdNew, 'fp', null);
        } else {
            if (betItem.priceType === 'sp' || betItem.priceType === 'boosted') {
                this.updateLegPriceTypeAndPrice(betId, 'fp');
            } else {
                this.onRemoveLeg(betId);
            }
        }
    };

    @action public onRemoveLeg = (betId: BetslipSingleId): void => {
        this.onResetCombinations();

        this.mutateLegs((map) => {
            map.delete(betId);
            // this.inputsSingle.getValue().get(betId).changeStake(new Amount(''));
        });
    };

    @action public onAddSPBet = (betIdNew: SelectionId): void => {
        const betId = this.ids.getSingleId(betIdNew, 0);

        const betItem = this.mapCorePrepareLegsValue.getValue().get(betId);
        if (betItem === undefined) {
            this.justAddBet(betIdNew, 'sp', null);
        } else {
            if (betItem.priceType === 'fp' || betItem.priceType === 'boosted') {
                this.updateLegPriceTypeAndPrice(betId, 'sp');
            } else {
                this.onRemoveLeg(betId);
            }
        }
    };

    @action public onAddBoostedBet = (betIdNew: SelectionId): void => {
        const betId = this.ids.getSingleId(betIdNew, 0);

        const betItem = this.mapCorePrepareLegsValue.getValue().get(betId);
        if (betItem === undefined) {
            this.justAddBet(betIdNew, 'boosted', null);
        } else {
            if (betItem.priceType !== 'boosted') {
                this.updateLegPriceTypeAndPrice(betId, 'boosted');
            } else {
                this.onRemoveLeg(betId);
            }
        }
    };

    @action public onAddCastBet = (betIdNew: SelectionId, index: number, sp: boolean): void => {
        const betId = this.ids.getSingleId(betIdNew, 0);

        const betItem = this.mapCorePrepareLegsValue.getValue().get(betId);
        if (betItem === undefined) {
            this.justAddBet(betIdNew, sp ? 'sp' : 'fp', index);
        } else {
            this.onRemoveLeg(betId);
        }
    };

    // @action public onChangeStake = (betId: BetslipSingleId, stakeValue: Amount): void => {
    //     this.mutateLegsItemIfExist(betId, (legItem): BetslipStateLegsType => {
    //         return {
    //             ...legItem,
    //             stakePerLine: stakeValue,
    //         };
    //     });
    // };

    @action public onChangeEachWay = (betId: BetslipSingleId, checked: boolean): void => {
        this.mutateLegsItemIfExist(betId, (legItem): BetslipStateLegsType => {
            return {
                ...legItem,
                eachWay: checked,
            };
        });
    };

    @action public deleteMapCorePrepareLegs = (): void => {
        this.mapCorePrepareLegsValue.setValue(new Map());
        // this.inputsSingle = newInputsSingle(this.decimalLength, this.onChangeStake);
        this.onResetCombinations();
    };

    @action public updateOrCreateLegFromBetRefferal = (
        betId: BetslipSingleId,
        priceType: 'fp' | 'sp' | 'boosted',
        stakePerLine: Amount,
        eachWay: boolean,
        selectionPrice: PriceType | null
    ): void => {
        this.mutateLegs((map) => {
            const betItem = map.get(betId);

            if (betItem === undefined) {
                const item: BetslipStateLegsType = {
                    selectionId: betId.id,
                    priceType,
                    stakePerLine: new StakeInputState2(this.decimalLength, () => {}),
                    eachWay,
                    index: null,
                    selectionPrice,
                    errors: [],
                };

                item.stakePerLine.changeStake(stakePerLine);
                map.set(betId, item);
            } else {
                const newItem = {
                    ...betItem,
                    priceType,
                    eachWay,
                    selectionPrice,
                };

                betItem.stakePerLine.changeStake(stakePerLine);

                map.set(betId, newItem);
            }
        });
    };

    // @action public setMapCorePrepareLegs = async (betIdOld: number, legModel: BetslipStateLegsType): Promise<void> => {
    //     const betId = await this.convertOldSelectionIdToNewId(betIdOld);

    //     this.mutateLegs((map) => {
    //         map.set(betId, legModel);
    //     });
    // };

    @action public setSelectionPrice = (betId: BetslipSingleId, selectionPrice: PriceType | null): void => {
        this.mutateLegsItemIfExist(betId, (legItem): BetslipStateLegsType => {
            return {
                ...legItem,
                selectionPrice,
            };
        });
    };

    public getSelectionPrice = (betId: BetslipSingleId): PriceType | null => {
        return this.mapCorePrepareLegsValue.getValue().get(betId)?.selectionPrice ?? null;
    };

    public getAllLegsIds = (): Array<BetslipSingleId> => {
        return [...this.mapCorePrepareLegsValue.getValue().keys()];
    };

    //-------------------------------------------------------------------------------------
    //combinations
    //-------------------------------------------------------------------------------------

    @action public onChangeCombinationStake = (combinationType: string, stakeValue: string): void => {
        this.mutateCombinationsItem(combinationType, (combinationItem): CombinationInputType => {
            return {
                ...combinationItem,
                stakePerLine: stakeValue,
            };
        });
    };

    @action public onChangeEachWayCombination = (combinationType: string, checked: boolean): void => {
        this.mutateCombinationsItem(combinationType, (combinationItem): CombinationInputType => {
            return {
                ...combinationItem,
                eachWay: checked,
            };
        });
    };

    @action public deleteMapCorePrepareCombinations = (): void => {
        this.mapCorePrepareCombinations.setValue(new Map());
    };

    public getCoreCombinationsPossibleBetsRequest = (): Record<string, CombinationInputType> => {
        if (this.referralState().referralData !== null) {
            return this.referralState().coreCombinationsPossibleBetsRequest;
        }

        const tempObj: Record<string, CombinationInputType> = {};

        for (const [key, elem] of this.mapCorePrepareCombinations.getValue().entries()) {
            // const combinationItem = this.possibleBetsRequestState.mapCorePrepareCombinationsFromResponse.get(key);

            // if (combinationItem !== undefined) {
            tempObj[key] = {
                // legs: combinationItem.legs,
                // potentialReturns: combinationItem.potentialReturns ?? '0',
                // price: combinationItem.price,
                // ewOffered: combinationItem.ewOffered ?? false,
                // name: combinationItem.name ?? '',
                // maxStake: combinationItem.maxStake,
                freeBets: elem.freeBets,
                stakePerLine: elem.stakePerLine,
                // potentialReturnsEw: elem.potentialReturns,
                // potentialReturnsAt: elem.stakePerLine,
                // numLines: combinationItem.numLines ?? 1,
                // type: combinationItem.type,
                eachWay: elem.eachWay,
                // freebetCredits: combinationItem.freebetCredits,
                // freebetRemarks: combinationItem.freebetRemarks,
            };
            // }
        }
        return tempObj;
    };

    //-------------------------------------------------------------------------------------
    //refs
    //-------------------------------------------------------------------------------------

    @action public setRef = (
        id: BetslipSingleId | BetslipCombinationId | BetslipRabId | BetslipAllSinglesId | BetslipMultibetId
    ): ((ref: HTMLElement | null) => void) => {
        const box = this.refs.get(id);
        return box.setRef;
    };

    public getRef = (
        id: BetslipSingleId | BetslipCombinationId | BetslipRabId | BetslipAllSinglesId | BetslipMultibetId
    ): HTMLElement | null => {
        return this.refs.get(id).refSelection;
    };

    //-------------------------------------------------------------------------------------
    //Updates from betRefferal
    //-------------------------------------------------------------------------------------

    @action public betReferralUpdateLeg = (selectionId: BetslipSingleId, leg: BetRefferalLegUpdateType): void => {
        if (
            leg.stakePerLine !== undefined &&
            leg.stakePerLine !== null &&
            !new Amount(leg.stakePerLine).isEqualWith(new Amount('0'))
        ) {
            const input = this.getInputSingle(selectionId);
            if (input === null) {
                console.error('betReferralUpdateLeg ignore input update');
            } else {
                input.changeStake(new Amount(leg.stakePerLine));
            }
        }

        if (this.getSelectionPrice(selectionId) === null) {
            this.setSelectionPrice(selectionId, leg.price ?? null);
        }
    };

    @action public betReferralUpdateCombination = (
        combinationId: string,
        combination: BetRefferalCombinationsUpdateType
    ): void => {
        if (combination.stakePerLine !== null && !new Amount(combination.stakePerLine).isEqualWith(new Amount('0'))) {
            this.getInputCombination(combinationId).changeStake(new Amount(combination.stakePerLine));
        }
    };
}
